import {
  FilledInputProps,
  InputProps,
  OutlinedInputProps,
} from '@material-ui/core';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { AsyncState } from 'react-use/lib/useAsyncFn';

export interface CustomSelectedValues {
  tableValues: TextFieldValue[];
  arbitraryValues?: TextFieldValue[];
}

export enum KindEnum {
  arbitrary = 'Arbitrary',
  system = 'System',
  domain = 'Domain',
  resource = 'Resource',
  consumable = 'Consumable',
  dataContract = 'DataContract',
  remote = 'Remote',
  policy = 'Policy',
}

export interface TextFieldValue {
  kind: KindEnum;
  label: string;
  isAiGenerated?: boolean;
  value?: string | GenericEntityType;
  kindDisplayName?: string;
}

export type Pagination = {
  limit: number;
  currentPage: number;
  cursor?: string;
  countlessOptions?: { hasNextPage: () => boolean; isFirstPage: () => boolean };
};

export type TemplateColumn = {
  name: string;
} & (
  | {
      path: string;
    }
  | {
      value: string;
    }
);

export type Column = {
  name: string;
  path: string;
  type?: string;
};
export interface GenericProviderType<T, F> {
  columns: Column[];
  entitiesState: AsyncState<T>;
  filters: F;
  changeFilters: <K extends keyof F>(key: K, value: F[K]) => void;
  resetFilters: () => void;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  count: number;
  nextCursor?: string;
  prevCursor?: string;
  templateKind?: TemplateEntity;
}

/**
 * Represents an entity that has been adapted to a common shape with name, kind, label, urn, and ref.
 */
export interface AdaptedEntity {
  name: string;
  kind: KindEnum;
  label: string;
  ref?: string;
  urn?: string;
}

export interface Provider<T, F> {
  useContext: () => GenericProviderType<T, F>;
  renderContextProvider: (
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
    parentTemplateRef?: string,
    formContext?: any,
  ) => JSX.Element;
  filtersSection: () => JSX.Element;
  /**
   * Resolve entity ref/urn or throw error if no entity id is found.
   *
   * @param ref - Entity ref or urn .
   * @returns
   * - `undefined`: If the ref is rejected, the entity provider cannot process it.
   * - `NotFoundException`: If the ref is handled but no entity is found
   * - An instance of @link {TextFieldValue} if the entity is found
   *
   * @throws {NotFoundException} If the reference was handled but entity doesn't exist.
   */
  resolve: (ref: string) => Promise<TextFieldValue | undefined>;
  /**
   * Adapts a raw entity retrieved from this provider to a common shape with name, kind, label, urn, and ref.
   */
  adapt: (entity: GenericEntityType) => AdaptedEntity;
  /**
   * Optional function to extract a unique identifier from a given entity.
   * This ID is used for comparing entities instead of relying on isEqual from lodash
   * TODO make it mandatory and be sure to support all providers return types etc.
   * @param entity - The entity object to extract the ID from.
   * @returns The unique ID as a string, or undefined if not available.
   */
  extractEntityId?: (entity: GenericEntityType) => string | undefined;
}
export interface GenericEntityType {
  __metadata: {
    name: string;
    kind: KindEnum;
  };
  [key: string]: any;
}

/**
 * The base configuration fields for each entity provider.
 */
export interface TemplateEntity {
  type: string;
  displayName?: string;
  returnField?: 'urn' | 'full' | 'ref';
  displayField?: string;
  userFilters?: string[];
  filter?: Record<string, any>;
  columns?: TemplateColumn[];
  apiSpec?: {
    retrieval?: {
      baseUrl: string;
      path: string;
      method?: string;
      params?: Record<string, any>;
    };
  };
  wittySuggestions?: { enabled: boolean };
  fieldsToSave?: string[];
}

export type BaseProps = {
  InputProps?:
    | Partial<InputProps>
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | undefined;
};
